import {omit} from 'lodash-es';
import {useEffect, useReducer, useState} from 'react';

import {ratesService} from '@/services/ratesService';
import type {AfriexAdminAmount, AfriexOTCRate, AfriexOTCTrade} from '@/types';
import {AfriexCurrencyCode} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {computeNewOTCRates} from '@/utils/otc';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import {OTCTradeActionType} from './types';
import updateTradeReducer from './updateTradeReducer';

const useOTCTradeUpdate = (
  onClose: VoidFunction,
  initialData: Partial<AfriexOTCTrade> = {
    fromSymbol: AfriexCurrencyCode.USD,
    toSymbol: AfriexCurrencyCode.NGN,
  },
) => {
  const [otcTrade, dispatch] = useReducer(
    updateTradeReducer,
    initialData as AfriexOTCTrade,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [initialOTCRates, setInitialOTCRates] = useState<AfriexOTCRate[]>([]);
  const [newOTCRates, setNewOTCRates] = useState<AfriexOTCRate[]>([]);
  const [newTradeRate, setNewTradeRate] = useState<number>(0);

  useEffect(() => {
    // This `.then` had no rejection handler at all. The rates it loads are the
    // baseline `computeNewOTCRates` derives the new OTC rates from, so a failed
    // fetch does not just log: it leaves the modal computing against an empty
    // baseline while looking like it loaded. Surfaced for that reason.
    ratesService
      .getOTCRatesList()
      .then(setInitialOTCRates)
      .catch((e: unknown) =>
        showErrorMessage(
          `Fetching OTC rates failed: ${getErrorMessage(e, String(e))}`,
        ),
      );
  }, []); // Empty dependency array for initial fetch only

  const updateTradeValues = (
    value: AfriexAdminAmount | Date | undefined,
    type: 'fromAmount' | 'toAmount' | 'date',
  ) => {
    const amounts = {
      fromAmount:
        type === 'fromAmount'
          ? (value as AfriexAdminAmount).amount
          : otcTrade.fromAmount,
      fromSymbol:
        type === 'fromAmount'
          ? (value as AfriexAdminAmount).currency
          : otcTrade.fromSymbol,
      toAmount:
        type === 'toAmount'
          ? (value as AfriexAdminAmount).amount
          : otcTrade.toAmount,
      toSymbol:
        type === 'toAmount'
          ? (value as AfriexAdminAmount).currency
          : otcTrade.toSymbol,
      // ISO strings, not Dates: the trade record carries `timestamp` as the
      // serialized string the API returns, and axios put the same ISO string
      // on the wire for a Date anyway.
      timestamp:
        type === 'date'
          ? ((value as Date | undefined)?.toISOString() ?? otcTrade.timestamp)
          : new Date().toISOString(),
    };

    if (amounts.fromAmount && amounts.toAmount && initialOTCRates.length > 0) {
      const {changedRates, newTradeRate} = computeNewOTCRates(
        amounts.fromAmount,
        amounts.toAmount,
        amounts.fromSymbol,
        amounts.toSymbol,
        initialOTCRates,
      );

      setNewOTCRates(changedRates);
      setNewTradeRate(newTradeRate);

      dispatch({
        type: OTCTradeActionType.UPDATE_OTC_TRADE,
        payload: {
          ...amounts,
          // `rate` is a string on the wire; admin used to type it as a number
          // and let Mongoose cast it on the way in. Every read site already
          // wraps it in `Number(...)`.
          rate: String(newTradeRate),
        },
      });
    } else {
      dispatch({
        type: OTCTradeActionType.UPDATE_OTC_TRADE,
        payload: amounts,
      });
    }
  };

  const handleSubmit = async (trades: AfriexOTCTrade[]) => {
    setIsLoading(true);
    try {
      if (trades.length === 0) {
        showErrorMessage('No trades to process');
        return;
      }

      // For creation (single trade)
      if (trades.length === 1 && !trades[0].id) {
        const trade = trades[0];
        if (
          !trade.fromSymbol ||
          !trade.toSymbol ||
          !trade.fromAmount ||
          !trade.toAmount
        ) {
          showErrorMessage('Please fill in all required fields');
          return;
        }

        await ratesService.createOTCTrade({
          otcRates: newOTCRates,
          otcVolume: trade,
        });
        showSuccessMessage(`OTC Trade created successfully`);
      }
      // For updates (single or multiple trades)
      else {
        const fieldsToOmit = [
          'submittedBy',
          'approvedBy',
          'createdAt',
          'updatedAt',
        ];
        const cleanedTrades = trades.map((t) => omit(t, fieldsToOmit));
        await ratesService.updateOTCTrade(cleanedTrades);
        showSuccessMessage(
          `OTC Trade${trades.length > 1 ? 's' : ''} updated successfully`,
        );
      }
    } catch (error) {
      showErrorMessage(
        `Error saving trade(s) ${getErrorMessage(error, String(error))}`,
      );
    } finally {
      onClose();
      setTimeout(() => window.location.reload(), 1000);
      setIsLoading(false);
    }
  };

  return {
    otcTrade,
    newTradeRate,
    dispatch,
    isLoading,
    updateTradeValues,
    handleSubmit,
    setNewTradeRate,
    initialOTCRates,
  };
};

export default useOTCTradeUpdate;
